import { CLASSES } from '../classes.ts';
import { RESURRECT_RANGE } from '../constants.ts';
import type { ClassId, Faction } from '../constants.ts';
import { Rng } from '../dice.ts';
import { dist, dist2d, forwardVector, rightVector, type Vec3 } from '../math.ts';
import { isEmbodied, tierInSlot, type PlayerState } from '../player.ts';
import { SLOT_ORDER, SPELLS_BY_ID } from '../spells.ts';
import { emptyInput, type PlayerInput, type SimState, type WorldQuery } from '../sim.ts';
import { centreOf, findPath, nearestOpen, type NavGrid } from './nav.ts';
import { buildInfluence, type InfluenceMap } from './influence.ts';
import { bestCell } from './appetite.ts';

/**
 * Bots.
 *
 * They produce `PlayerInput` and nothing else — the same struct a keyboard
 * produces — so they are held to every rule a player is: cooldowns, the global
 * cooldown, line of sight, locked slots at low level, and the same spread when
 * they move. It also means the authoritative server can run them unchanged, and
 * that a bot can never desync because there is nothing to desync.
 *
 * They are meant to be plausible rather than clever. They walk somewhere
 * sensible, shoot what is in front of them, take the objectives their class can
 * actually affect, and run when they are nearly dead. That is enough to make a
 * match feel alive, which is what solo testing needs.
 */

export interface BotSkill {
  /** Aim error at the moment of firing, radians of cone. */
  aimError: number;
  /** How long before a newly sighted enemy is fired upon. */
  reaction: number;
  /** How long a bot keeps aiming at where somebody went after losing sight. */
  memory: number;
  /** 0 = fires where the target is, 1 = fires where they will be. */
  lead: number;
  /** Decisions a second. Aim runs every tick regardless. */
  thinkRate: number;
}

/**
 * How long a ghost waits for a Cleric before walking to Valhalla.
 *
 * A shade under the Call Dead cooldown, so a ghost gives one Cleric one window
 * and then stops holding the queue up for everybody behind it.
 */
const GHOST_PATIENCE = 7;

export const BOT_SKILLS: Record<'easy' | 'normal' | 'hard', BotSkill> = {
  easy: { aimError: 0.075, reaction: 0.65, memory: 1.5, lead: 0.2, thinkRate: 3 },
  // The house setting: good but not inhuman. A tenth of a second of hesitation
  // and about two degrees of scatter is enough to be beatable while still
  // punishing anyone who stands in the open.
  normal: { aimError: 0.035, reaction: 0.28, memory: 2.5, lead: 0.6, thinkRate: 5 },
  hard: { aimError: 0.014, reaction: 0.12, memory: 3, lead: 0.85, thinkRate: 8 },
};

/** How a class spends its match. */
type Doctrine = 'hold-shrine' | 'hold-pools' | 'push' | 'hunt';

/**
 * What each discipline is for.
 *
 * The Arcanist cannot bias pools at all, so sending one to stand in a pool is
 * sending it to do nothing; the Cleric belongs behind the line where it can raise
 * people rather than in front of it. Doctrine is the cheapest possible way to
 * make four classes behave like four classes.
 */
/**
 * How long a bot goes on believing it is being shot at after the last hit.
 *
 * Short enough that it stands back up once a fight moves on, long enough to
 * cover the gap between two darts rather than bobbing between stances.
 */
const UNDER_FIRE_MEMORY = 2.5;

/** Fatigue a Mentalist keeps back, so it can still cast when it lands. */
const FLY_FATIGUE_FLOOR = 35;
/** Metres above the floor: enough to open an angle, fighting. */
const FLY_FIGHT_HEIGHT = 4;
/** And enough to put something solid between you and a chase, leaving. */
const FLY_ESCAPE_HEIGHT = 7;

/**
 * How near a friendly has to be to count as support.
 *
 * Roughly a room. The distinction the ladders keep coming back to is whether
 * anybody is *with* you — a lone mage pushing into held ground dies for nothing,
 * and the same mage with two friends beside it is an attack. Every class asks
 * this question and they should all mean the same thing by it.
 */
const SUPPORT_RANGE = 16;

/** Below this a bot is worth disengaging over, whatever else it was doing. */
const BADLY_HURT = 0.3;

/**
 * How close two friendlies get before they start pushing apart, and how hard.
 *
 * Bots converging on the same objective arrive at the same point, and a heap of
 * mages is one Fire Ball. This is not formation-keeping — it is the smallest
 * thing that stops a cluster being a single target, applied as a nudge on the
 * movement input so it costs nothing and cannot deadlock a path.
 */


/** Crowding starts here and does not stop until `SPACING_EXIT`. */
const SPACING_ENTER = 3.0;
/**
 * And the same, for a mage standing on something worth standing on.
 *
 * Barely more than shoulders. On a pool or a shrine the right answer is to be
 * close — that is the ground being contested — so this asks only that two mages
 * are not in the same square.
 */
const OBJECTIVE_ENTER = 1.2;
const OBJECTIVE_EXIT = 1.9;
/** How long a push may achieve nothing before it is abandoned. */
const SPACING_FUTILE = 1.6;
const SPACING_EXIT = 4.0;
/** Quiet spell after separating, so a brush past does not start a shoving match. */
/**
 * How far from an unreachable goal a bot will settle for instead.
 *
 * Generous enough to cover a mage standing in a doorway or against a wall, tight
 * enough that "the nearest walkable square" to something genuinely out of the
 * world is not somewhere across the arena.
 */
const UNREACHABLE_GIVE_UP = 12;

/**
 * How close to a shrine counts as being on it, for the purpose of not shuffling.
 *
 * The glowing field rather than the room: a Cleric holding a *corner* of its
 * shrine room should still spread out, and only one standing on the jewel is
 * doing something that being nudged would interrupt.
 */
const SHRINE_FIELD = 4;

/**
 * Seconds between rebuilding the team's view of the arena.
 *
 * The map describes the *shape* of the match — who holds what, where the front
 * is — and that does not change at sixty hertz. Once a second is more than the
 * bots can react to anyway: their movement decisions are held for a second and a
 * half regardless, so a survey twice that often was rebuilding a picture nobody
 * would consult before it was rebuilt again.
 *
 * This is the server's largest per-room cost by a wide margin — the simulation
 * itself is a tenth of a millisecond a frame and the bots are most of the rest —
 * so halving it roughly doubles the rooms a core can hold. Reaction time is
 * unaffected, because reacting to what a bot can *see* runs on the think tick
 * and never touched this.
 */
const SURVEY_EVERY = 1.0;

/**
 * How close another bot's chosen square has to be before it puts you off, and
 * how much. Big enough to spread a team across a room, small enough that two
 * mages can still hold one pool between them.
 */
const CLAIM_RADIUS = 5;
const CLAIM_COST = 3.5;

/** What an Arcanist pays for standing about at its own shrine. */
const HOMEBODY_COST = 3;

/** What a Cleric gains for staying within reach of the shrine it guards. */
const HOMEBODY_PULL = 2.4;

/**
 * How long a movement decision stands before it may be revisited.
 *
 * Deliberately long. A bot that reconsiders five times a second and has any
 * reason at all to alternate will alternate, and the result is a flicker faster
 * than a hand could produce it.
 */
const GOAL_HOLD = 1.5;

/** How near a bot's goal counts as standing on it. */
const ARRIVED = 2.5;

/**
 * How fast a bot may turn, radians per second.
 *
 * A bot writes its aim straight into `yaw`, so it could always spin instantly —
 * which is both unfair and unmistakably a machine. Roughly three hundred degrees
 * a second is a fast human flick and nowhere near an aimbot's.
 */
const TURN_RATE = 5.2;

/** Roughly how long a bot strafes one way before changing its mind. */
const JINK_HOLD = 0.9;

/**
 * How far each class looks when choosing where to stand.
 *
 * A Cleric has no business scoring the far side of the arena — it would find
 * something worth having and walk away from the people it exists to keep alive.
 * An Arcanist very much does: roaming *is* the class, and a bounded search is
 * what would pin it to one quarter of the map.
 */
const SEARCH_RADIUS: Record<ClassId, number> = {
  cleric: 26,
  magician: 40,
  mentalist: 55,
  arcanist: 90,
};

/**
 * How hard the director's instructions pull, against everything else on the map.
 *
 * `beat` is the pool a Magician has been given, so two do not claim one. `focus`
 * is the objective a side has been told to press, and is the only thing that
 * breaks a stand-off — both sides correctly reading even odds will sit there
 * forever otherwise.
 */
const BEAT_PULL = 2.2;
const FOCUS_PULL = 1.6;

/**
 * What a mage this bot can actually see is worth, per class.
 *
 * The map is half a second old and shared; sight is now and personal. A Cleric
 * is barely moved by it — seeing an enemy is a reason to be elsewhere — where an
 * Arcanist reorganises its whole plan around one.
 */
const SIGHT_PULL: Record<ClassId, number> = {
  cleric: 0.2,
  magician: 0.8,
  mentalist: 1.8,
  arcanist: 2.4,
};

/** Below this the Arcanist breaks off and goes to find water. */
const ARCANIST_TOP_UP = 0.45;

/** How far a sentry looks when deciding which way is open. */
const FACE_PROBE = 8;
const SPACING_REST = 1.2;
const SPACING_PUSH = 1.1;
/**
 * The fastest a bot separates, as a fraction of its walk.
 *
 * A third of a walk. Fast enough that two mages who have converged are apart
 * within a couple of seconds, slow enough that neither overshoots the distance
 * it was aiming for — which is the difference between drifting apart and
 * oscillating across a threshold several times a second.
 */
const SPACING_DRIFT = 0.34;

const DOCTRINE: Record<ClassId, Doctrine> = {
  cleric: 'hold-shrine',
  magician: 'hold-pools',
  mentalist: 'push',
  arcanist: 'hunt',
};

/** Per-bot memory. Nothing here affects the simulation; it only shapes input. */
interface BotMind {
  skill: BotSkill;
  rng: Rng;
  thinkIn: number;
  path: Vec3[];
  goal: Vec3 | null;
  targetId: string | null;
  /** Where the target was last seen, and how long ago. */
  lastSeen: Vec3 | null;
  lostFor: number;
  /** Counts down before a newly acquired target is fired upon. */
  reactionLeft: number;
  /** How long this ghost has been waiting on a Cleric. */
  hauntedFor: number;
  /**
   * Where a Cleric stands to guard its shrine.
   *
   * Chosen once and kept, or a bot picks a different spot every time it thinks
   * and spends the match shuffling between two pieces of cover.
   */
  post: Vec3 | null;
  /**
   * Aim wander.
   *
   * Re-rolled a couple of times a second rather than every tick or once per
   * engagement. Per tick it averages out to perfect aim; once per engagement is
   * worse — the roll either lands on the target or it does not, so a bot hits
   * every shot for the whole fight or misses every one of them. Drifting is what
   * actually reads as a hand that is not quite steady.
   */
  /**
   * Seconds since this bot last lost hit points, and what it had last tick.
   *
   * "Under fire" is not the same question as "can I see somebody" — a Magician
   * holding a pool can watch a fight across the nave without being in it, and
   * ducking for that is how a bot spends a match crouched. Damage taken is the
   * signal that something is actually coming at *you*.
   */
  hurtAgo: number;
  hpLast: number;
  /** Floor level the last time this bot was standing on it, to measure climb by. */
  groundY: number | null;
  /** Where nearby friendlies were on the last thinking tick, for spacing. */
  crowd: Vec3[];
  /** Which stop on the roaming circuit this bot is heading for. Arcanists only. */
  roamTo: number | null;
  /** Whether this bot is currently trying to get out of somebody's way. */
  spacingUrge: boolean;
  /** Seconds left of not re-deciding that, after separating. */
  spacingRest: number;
  /** How long the current push has been achieving nothing, and the gap it began at. */
  spacingFor: number;
  spacingWas: number;
  /** Seconds until this bot may reconsider where it is going. */
  goalIn: number;
  /** Which way this bot is currently sidestepping, and for how much longer. */
  jinkSide: 1 | -1;
  jinkFor: number;
  aimOffset: { yaw: number; pitch: number };
  aimHoldFor: number;
  slot: number;
}

/**
 * Turns `from` toward `to`, no faster than a hand could.
 *
 * Bots write their aim straight into yaw, so without this they snap. Snapping is
 * both unfair — no lead ever escapes it — and the single most obvious tell that
 * a thing is not a person. Wrapped through the short way round, so turning past
 * north does not become a lap of the compass.
 */
export function turnToward(from: number, to: number, dt: number): number {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;

  const most = TURN_RATE * dt;
  if (Math.abs(delta) <= most) return to;
  return from + Math.sign(delta) * most;
}

export class BotDirector {
  private readonly minds = new Map<string, BotMind>();
  /** The team's read of the arena. Shared by every bot the director runs. */
  private map: InfluenceMap | null = null;
  private surveyIn = 0;
  /** Where each side has been told to press. */
  private readonly focus = new Map<Faction, Vec3>();
  /** The pool a given Magician has been given, so two do not claim one. */
  private readonly beat = new Map<string, Vec3>();
  /**
   * Where each bot has decided to stand.
   *
   * Two mages reading the same map reach the same conclusion, which is correct
   * and useless: they walk to the identical square and stand in it together. The
   * influence map cannot see this, because a decision is not a presence — nobody
   * is there yet. So the decisions themselves are shared, and a square somebody
   * has already called dibs on is worth less to everybody else.
   */
  private readonly claimed = new Map<string, Vec3>();

  private nav: NavGrid;
  private readonly world: WorldQuery;
  private readonly difficulty: keyof typeof BOT_SKILLS;
  private readonly seed: number;

  // Written out rather than declared as constructor parameter properties: Node's
  // strip-only TypeScript cannot parse those, and a file that uses one cannot be
  // imported by a test at all — which is a large part of why the renderer has
  // stayed the blind spot it is.
  /**
   * Takes a new map, for a rotation between rounds.
   *
   * Everything cached is about the map that has gone: routes across it, which
   * pool each Magician was sent to, where each bot decided to stand, and the
   * influence read that produced all three. Kept, they would send bots walking
   * confidently through a building that is not there any more — the failure
   * would look like bots being broken rather than like a stale grid.
   */
  repath(nav: NavGrid): void {
    this.nav = nav;
    this.map = null;
    this.surveyIn = 0;
    this.focus.clear();
    this.beat.clear();
    this.claimed.clear();
    for (const mind of this.minds.values()) {
      mind.path = [];
      mind.goal = null;
      // Think again on the next tick rather than finishing a thought about a
      // map that no longer exists.
      mind.thinkIn = 0;
    }
  }

  constructor(
    nav: NavGrid,
    world: WorldQuery,
    difficulty: keyof typeof BOT_SKILLS = 'normal',
    seed = 0xb07,
  ) {
    this.nav = nav;
    this.world = world;
    this.difficulty = difficulty;
    this.seed = seed;
  }

  /** One input per bot, ready to be handed to `step` alongside the player's. */
  inputs(state: SimState, ids: readonly string[], dt: number): Map<string, PlayerInput> {
    this.survey(state, dt);
    const out = new Map<string, PlayerInput>();
    for (const id of ids) {
      const self = state.players.find((p) => p.id === id);
      if (!self) continue;
      out.set(id, this.think(state, self, dt));
    }
    return out;
  }

  /**
   * The team's view of the arena, rebuilt a few times a second for everybody.
   *
   * This is the layer the bots never had. Each one used to reason alone from
   * distances, which is why two Magicians would independently reach the same
   * correct conclusion about the same pool while the enemy's two did the same
   * about theirs, and nothing ever moved. Nobody can talk themselves out of a
   * stand-off from inside it; somebody has to be told.
   *
   * Twice a second is plenty. The map is about what the *shape* of the match is,
   * and that does not change at sixty hertz.
   */
  private survey(state: SimState, dt: number): void {
    this.surveyIn -= dt;
    if (this.map && this.surveyIn > 0) return;
    this.surveyIn = SURVEY_EVERY;

    this.assign(state);
    this.map = buildInfluence(this.nav, state.arena, state.players, this.focus);
  }

  /**
   * Names one objective per side as the thing to press, and shares out the rest.
   *
   * Two jobs, and they are the same job. Naming a focus is what breaks a
   * stand-off: four mages attacking one pool is a different sum from one mage
   * attacking it, and the influence map only knows that if somebody raises the
   * prize. Sharing out the rest is what stops two Magicians claiming the same
   * water while a third pool sits unattended.
   *
   * Deliberately crude. A good assignment that everyone follows beats a clever
   * one nobody can be made to obey, and every bot is still free to ignore this
   * when something is shooting at it.
   */
  private assign(state: SimState): void {
    this.focus.clear();

    for (const faction of ['order', 'balance', 'chaos'] as const) {
      const mine = state.players.filter(
        (p) => p.faction === faction && isEmbodied(p),
      );
      if (!mine.length) continue;

      /*
       * The cheapest ground worth taking: not ours, and not the one the enemy
       * has most people standing on. `held` counts everybody so a pool ringed by
       * two factions still reads as expensive — a three-way fight is not an
       * opportunity for anybody.
       */
      const wanted = state.arena.pools
        .filter((pool) => pool.owner !== faction)
        .map((pool) => {
          const held = state.players.filter(
            (p) => isEmbodied(p) && p.faction !== faction
              && dist2d(p.pos, pool.pos) < 12,
          ).length;
          const reach = Math.min(...mine.map((p) => dist2d(p.pos, pool.pos)));
          return { pool, cost: reach + held * 14 };
        })
        .sort((a, b) => a.cost - b.cost)[0];

      if (wanted) this.focus.set(faction, { ...wanted.pool.pos });

      // One Magician per pool, nearest first, so they fan out instead of
      // convoying. Anything past the number of pools falls back to the focus.
      const claimed = new Set<string>();
      for (const mage of mine.filter((p) => p.cls === 'magician')) {
        const pick = state.arena.pools
          .filter((pool) => !claimed.has(pool.id))
          .map((pool) => ({ pool, d: dist2d(mage.pos, pool.pos) }))
          .sort((a, b) => a.d - b.d)[0];
        if (!pick) break;
        claimed.add(pick.pool.id);
        this.beat.set(mage.id, { ...pick.pool.pos });
      }
    }
  }

  /**
   * What a ghost does.
   *
   * Drifts toward the nearest living Cleric on its own side and waits to be
   * called, because being called back costs nothing and the walk through
   * Valhalla costs eight seconds and a share of the experience. With no Cleric
   * left standing there is nothing to wait for, so it takes the walk at once
   * rather than hovering over its own corpse for the rest of the match.
   */
  private haunt(
    state: SimState, self: PlayerState, mind: BotMind, input: PlayerInput, dt: number,
  ): PlayerInput {
    mind.targetId = null;
    if (self.inValhalla) return this.walkValhalla(state, self, input);

    const cleric = state.players
      .filter((p) => p.faction === self.faction && isEmbodied(p)
        && CLASSES[p.cls].id === 'cleric')
      .map((p) => ({ p, d: dist2d(self.pos, p.pos) }))
      .sort((a, b) => a.d - b.d)[0];

    /*
     * Patience, then the walk.
     *
     * Waiting for a Cleric who never comes is worse than the corridor: Call Dead
     * has an eight-second cooldown, so one Cleric and three ghosts means two of
     * them are standing about for the better part of half a minute. A ghost gives
     * the living a few seconds and then takes itself home.
     */
    mind.hauntedFor += dt;
    if (!cleric || mind.hauntedFor > GHOST_PATIENCE) {
      input.selfResurrect = true;
      return input;
    }

    mind.thinkIn -= dt;
    if (mind.thinkIn <= 0) {
      mind.thinkIn = 1 / mind.skill.thinkRate;
      const goal = { ...cleric.p.pos };
      if (!mind.goal || dist2d(mind.goal, goal) > 3) {
        mind.goal = goal;
        // Ghosts drift over the arena rather than through it, so they are given
        // the straight line and not a path.
        mind.path = [goal];
      }
    }

    // Already beside them: stand still and wait to be called rather than
    // orbiting, which looks like indecision and gets in the Cleric's way.
    if (cleric.d < 3) {
      input.yaw = self.yaw;
      return input;
    }
    this.aimAt(self, mind.path[0] ?? cleric.p.pos, input);
    this.move(state, self, mind, input, dt);
    return input;
  }

  /**
   * The long walk, toward the teleporter rather than in a hopeful direction.
   *
   * This used to set yaw to zero and call it forward. Yaw zero points along −Z,
   * and the Cathedral's corridor runs from −28 to +28 — so every bot that took
   * the walk marched into the wall behind it and stayed there for the rest of
   * the match. Steering at the exit is correct here and survives an arena laying
   * its corridor out some other way.
   *
   * There were two copies of this, one for ghosts and one for the living, and
   * only one of them was ever reached: a mage in Valhalla is *alive*. Hence one
   * method, called from both.
   */
  private walkValhalla(state: SimState, self: PlayerState, input: PlayerInput): PlayerInput {
    const exit = state.arena.valhalla[0]?.exits[0];
    if (!exit) return input;

    this.aimAt(self, exit, input);
    input.moveForward = 1;
    input.run = true;
    return input;
  }

  private mindFor(id: string): BotMind {
    let mind = this.minds.get(id);
    if (!mind) {
      mind = {
        skill: BOT_SKILLS[this.difficulty],
        // Seeded per bot, so twenty of them do not all twitch in unison and the
        // whole thing stays reproducible from the match seed.
        rng: new Rng(this.seed + id.length * 7919 + id.charCodeAt(id.length - 1) * 31),
        // Staggered, so twenty bots do not all plan on the same tick. Cheap
        // insurance now that pathfinding is fast, and it stops a whole team
        // turning in unison, which reads as a formation rather than a rabble.
        thinkIn: (id.length * 37 % 100) / 100 / BOT_SKILLS[this.difficulty].thinkRate,
        hauntedFor: 0,
        post: null,
        path: [],
        goal: null,
        targetId: null,
        lastSeen: null,
        lostFor: 0,
        reactionLeft: 0,
        // Large enough that a freshly spawned bot is not treated as having just
        // been shot; hp is seeded on the first tick that sees the player.
        hurtAgo: 99,
        hpLast: -1,
        groundY: null,
        crowd: [],
        roamTo: null,
        spacingUrge: false,
        spacingRest: 0,
        spacingFor: 0,
        spacingWas: Infinity,
        goalIn: 0,
        jinkSide: 1,
        jinkFor: 0,
        aimOffset: { yaw: 0, pitch: 0 },
        aimHoldFor: 0,
        slot: 0,
      };
      this.minds.set(id, mind);
    }
    return mind;
  }

  private think(state: SimState, self: PlayerState, dt: number): PlayerInput {
    const mind = this.mindFor(self.id);
    const input = emptyInput();

    if (!self.alive || self.ghost) return this.haunt(state, self, mind, input, dt);
    mind.hauntedFor = 0;
    if (self.inValhalla) return this.walkValhalla(state, self, input);

    mind.thinkIn -= dt;
    const replan = mind.thinkIn <= 0;
    if (replan) mind.thinkIn = 1 / mind.skill.thinkRate;

    this.trackTarget(state, self, mind, dt, replan);
    this.choosePath(state, self, mind, dt);

    /*
     * Who is standing near, refreshed every tick.
     *
     * This used to live at the top of `chooseGoal`, which was fine while that
     * ran five times a second and became a bug the moment movement decisions
     * started being held for a second and a half: the spacing nudge runs every
     * frame and was reading a list that had not been updated since the last time
     * the bot changed its mind. Two mages that drifted together after deciding
     * therefore never noticed.
     */
    mind.crowd = this.support(state, self).map((p) => ({ ...p.pos }));

    this.aim(state, self, mind, input, dt);
    this.move(state, self, mind, input, dt);
    this.posture(state, self, mind, input, dt);
    this.act(state, self, mind, input, replan);
    return input;
  }

  // ── How to stand ───────────────────────────────────────────────────────────

  /**
   * Crouching and flight — the two things a bot could do and never did.
   *
   * Kept apart from `move`, which decides *where* to go, because these decide
   * how to carry yourself while going there and the two answer to different
   * things. Both run after `move` so they can override what it asked for: a
   * crouching bot must not also be sprinting.
   */
  private posture(
    state: SimState, self: PlayerState, mind: BotMind, input: PlayerInput, dt: number,
  ): void {
    // How long since something actually hurt. Seeded rather than assumed on the
    // first tick, or every bot begins the match believing it has just been shot.
    if (mind.hpLast < 0) mind.hpLast = self.hp;
    if (self.hp < mind.hpLast) mind.hurtAgo = 0;
    else mind.hurtAgo += dt;
    mind.hpLast = self.hp;
    const underFire = mind.hurtAgo < UNDER_FIRE_MEMORY;

    if (DOCTRINE[self.cls] === 'hold-pools') this.holdLow(state, self, input, underFire);
    if (CLASSES[self.cls].canFly) this.takeToTheAir(self, mind, input, underFire);
  }

  /**
   * A Magician ducks in the pool it is holding.
   *
   * The doctrine already says a Magician's job is to stand in a pool, and
   * standing in one is the least evasive thing a mage can do — it cannot leave
   * without giving up what it came for, so the only defence available is to be a
   * smaller target. Crouching costs speed, which is the one thing it does not
   * need while deliberately not going anywhere.
   *
   * Conditioned on actually being in the pool, not merely near it. A bot that
   * crouches on the walk over arrives late and out of position, having spent the
   * trip at a third of its speed for no benefit at all.
   */
  private holdLow(
    state: SimState, self: PlayerState, input: PlayerInput, underFire: boolean,
  ): void {
    if (!underFire) return;
    // Hurt badly enough and the answer is to leave rather than to hunker, which
    // is what `chooseGoal` has already decided. Crouching would fight it.
    if (self.hp < self.hpMax * 0.3) return;

    const standing = state.arena.pools.some(
      (pool) => dist2d(self.pos, pool.pos) <= pool.innerRadius,
    );
    if (!standing) return;

    input.crouch = true;
    // Crouching and running are contradictory instructions, and `move` may have
    // asked for the second.
    input.run = false;
  }

  /**
   * A Mentalist gets off the ground, to shoot from or to leave by.
   *
   * Height is the class's whole argument: it opens angles nothing else in the
   * game can take and it breaks a chase, because the pursuer has to go round
   * whatever the Mentalist went over. Both uses are here — climbing to fight and
   * climbing to run — and they are the same input.
   *
   * Fatigue is what makes it a decision rather than a default. Flight drains it
   * fast and an exhausted mage cannot cast at all, so a bot that flew whenever
   * it could would arrive at every fight unable to do anything once there. It
   * keeps enough in hand to still be a threat when it lands.
   */
  private takeToTheAir(
    self: PlayerState, mind: BotMind, input: PlayerInput, underFire: boolean,
  ): void {
    if (self.fatigue < FLY_FATIGUE_FLOOR) return;

    const fleeing = self.hp < self.hpMax * 0.3 && underFire;
    const fighting = mind.targetId !== null && mind.lostFor < 1;
    if (!fleeing && !fighting) return;

    // Enough to clear a wall and see over the crowd, not enough to become a
    // silhouette against the ceiling with nothing to break line of sight.
    const ceiling = fleeing ? FLY_ESCAPE_HEIGHT : FLY_FIGHT_HEIGHT;
    const climbed = self.pos.y - (mind.groundY ?? self.pos.y);
    if (self.onGround) mind.groundY = self.pos.y;

    input.flyUp = climbed < ceiling;
    // Coming down is deliberate rather than a fall: a Mentalist that simply
    // stops flying drops, and dropping is when it is easiest to hit.
    input.flyDown = climbed > ceiling + 1;
  }

  // ── Who to shoot ───────────────────────────────────────────────────────────

  /**
   * Keeps aiming at where somebody went for a couple of seconds after they break
   * line of sight, which is most of what stops a bot reading as a turret that
   * switches off the instant you step behind a pillar.
   */
  private trackTarget(
    state: SimState, self: PlayerState, mind: BotMind, dt: number, replan: boolean,
  ): void {
    const eye = { x: self.pos.x, y: self.pos.y + 1.55, z: self.pos.z };
    const current = mind.targetId
      ? state.players.find((p) => p.id === mind.targetId)
      : undefined;

    const visible = (p: PlayerState): boolean =>
      isEmbodied(p)
      && p.faction !== self.faction
      && dist(self.pos, p.pos) <= 45
      && !this.world.segmentBlocked(eye, { x: p.pos.x, y: p.pos.y + 1.1, z: p.pos.z });

    if (current && visible(current)) {
      mind.lastSeen = { ...current.pos };
      mind.lostFor = 0;
      mind.reactionLeft = Math.max(0, mind.reactionLeft - dt);
      return;
    }

    mind.lostFor += dt;
    // Only look for somebody new on a thinking tick, so a bot does not swap
    // target every frame when two enemies are equidistant.
    if (replan || !current) {
      let best: PlayerState | null = null;
      let bestScore = Infinity;
      for (const p of state.players) {
        if (!visible(p)) continue;
        /*
         * Nearest, except that an Arcanist would rather have the Magician.
         *
         * A Magician standing in water is the reason the ground is changing
         * hands, and the Arcanist is the class that can fight in that water. A
         * discount rather than a rule: something about to kill it still outranks
         * a preference.
         */
        const prefer = self.cls === 'arcanist' && p.cls === 'magician' ? 0.6 : 1;
        const d = dist(self.pos, p.pos) * prefer;
        if (d >= bestScore) continue;
        best = p;
        bestScore = d;
      }
      if (best && best.id !== mind.targetId) {
        mind.targetId = best.id;
        mind.lastSeen = { ...best.pos };
        mind.lostFor = 0;
        mind.reactionLeft = mind.skill.reaction;
        mind.aimHoldFor = 0;
        return;
      }
      if (best) {
        mind.lastSeen = { ...best.pos };
        mind.lostFor = 0;
        mind.reactionLeft = Math.max(0, mind.reactionLeft - dt);
        return;
      }
    }

    if (mind.lostFor > mind.skill.memory) {
      mind.targetId = null;
      mind.lastSeen = null;
    }
  }

  // ── Where to go ────────────────────────────────────────────────────────────

  private choosePath(
    state: SimState, self: PlayerState, mind: BotMind, dt: number,
  ): void {
    mind.goalIn -= dt;

    /*
     * Where to stand is re-decided far more slowly than what to shoot.
     *
     * They used to share the think tick — five times a second — and the flicker
     * came straight out of that. Two bots avoiding each other's chosen squares
     * swap their conclusions on the next tick and swap back on the one after, so
     * a mage changes direction twice in four hundred milliseconds, which nothing
     * with hands could do and nothing watching reads as deliberate.
     *
     * A decision that stands for a second and a half is a decision. Aiming and
     * target selection stay fast, because those *are* reflexes.
     */
    const duty = this.duty(state, self);
    const pressing = duty !== null && (mind.goal === null || dist2d(mind.goal, duty) > 5);

    /*
     * Having arrived is not a reason to think again.
     *
     * The hold was bypassed whenever the path was empty, which is true of every
     * bot standing where it meant to stand — so an *arrived* bot re-decided
     * sixty times a second while a walking one decided every second and a half.
     * Alone that is invisible: it picks the same square each time. With a
     * teammate it is the wiggle, because each is avoiding the other's claimed
     * square and the pair can swap conclusions on consecutive ticks for as long
     * as they both stand there.
     *
     * An empty path now only forces a rethink when the bot is *not* where it
     * wanted to be — which is the case the bypass was for: a goal that could not
     * be reached, and standing still for a second and a half over it.
     */
    const arrived = mind.goal !== null && dist2d(self.pos, mind.goal) < ARRIVED;
    if (!pressing && mind.goalIn > 0 && (mind.path.length || arrived)) return;
    mind.goalIn = GOAL_HOLD;

    const goal = this.chooseGoal(state, self, mind);
    if (!goal) return;

    // Only re-path when the destination has actually moved. Re-running A* every
    // think tick for a goal that has not changed is the expensive way to stand
    // still.
    if (mind.goal && dist2d(mind.goal, goal) < 3 && mind.path.length) return;

    mind.goal = goal;
    mind.path = findPath(this.nav, self.pos, goal);

    /*
     * A goal nothing can walk to leaves a bot standing still for ever.
     *
     * Most goals are places somebody chose — a shrine, a pool, a post — and
     * those are reachable by construction. But `lastSeen` is wherever an enemy
     * happened to be, and an enemy can be somewhere a path cannot end: inside
     * the radius of a wall, on the wrong side of a doorway too tight for the
     * grid's clearance, mid-air under a flying Mentalist. `findPath` returns
     * nothing, `move` has no waypoint, and the bot stops for the rest of the
     * match — which is what "standing off to the side doing nothing" looked
     * like, for every class rather than only the Arcanist.
     *
     * So an unreachable goal falls back to the nearest square that *is* walkable,
     * which is close enough to be useful and, unlike standing still, is a
     * decision.
     */
    if (mind.path.length) return;
    const near = nearestOpen(this.nav, goal);
    if (!near) return;
    const stand = centreOf(this.nav, near.col, near.row);
    if (dist2d(stand, goal) > UNREACHABLE_GIVE_UP) return;
    mind.goal = stand;
    mind.path = findPath(this.nav, self.pos, stand);
  }

  private chooseGoal(state: SimState, self: PlayerState, mind: BotMind): Vec3 | null {
    const home = state.arena.shrines.find((sh) => sh.faction === self.faction);


    /*
     * Retreat, but not at the first scratch and not alone.
     *
     * A flat "below thirty per cent, go home" made every class disengage the
     * moment a fight turned, which is how a whole team ends up walking backwards
     * out of a fight it was winning. Friends nearby are worth staying for: it is
     * being hurt *and* isolated that means leave.
     */
    const alone = this.support(state, self).length === 0;
    const spent = self.hp < self.hpMax * BADLY_HURT;
    if (spent && (alone || self.hp < self.hpMax * 0.15) && home) return { ...home.pos };

    // Home under threat outranks everything, for every class.
    if (home && !home.conquered) {
      const enemyNear = state.players.some(
        (p) => isEmbodied(p) && p.faction !== self.faction && dist2d(p.pos, home.pos) < 18,
      );
      if (enemyNear) return { ...home.pos };
    }

    const map = this.map;
    if (!map) return home ? { ...home.pos } : null;

    /*
     * Duties first, then the map.
     *
     * A Cleric that will not stop to raise a ghost is not a Cleric however
     * sensibly it positions, and an Arcanist that runs dry has no spells to be
     * clever with. These are the handful of things that outrank reading the
     * room — not a ladder, just the short list of jobs that are the class.
     * Everything else is one question asked of one map.
     */
    const duty = this.duty(state, self);
    if (duty) return duty;

    const beat = this.beat.get(self.id);
    const focus = this.focus.get(self.faction);

    /*
     * Sight is what the map cannot give you.
     *
     * The influence map is a shared, half-second-old picture of the shape of the
     * match. It knows where the fighting is; it does not know that *this* mage
     * has somebody in front of it right now. So a visible enemy sweetens the
     * ground around it, and the class curve decides whether that is enough.
     */
    const seen = mind.lastSeen;
    const others = [...this.claimed.entries()]
      .filter(([id]) => id !== self.id)
      .map(([, at]) => at);

    /*
     * A Cleric's ground is measured from its shrine; everybody else's from
     * wherever they are. It is the difference between "stay near home" and "do
     * not walk far in one go", and only one of those is a doctrine.
     */
    const anchor = self.cls === 'cleric' && home ? home.pos : undefined;

    const pick = bestCell(map, self, SEARCH_RADIUS[self.cls], (at) => {
      let extra = 0;
      if (beat) extra += Math.max(0, 1 - dist2d(at, beat) / 10) * BEAT_PULL;
      if (focus) extra += Math.max(0, 1 - dist2d(at, focus) / 14) * FOCUS_PULL;
      if (seen) extra += Math.max(0, 1 - dist2d(at, seen) / 12) * SIGHT_PULL[self.cls];
      /*
       * Only the nearest claim counts, not the sum of them.
       *
       * Summed, a team converging on one objective repels its own last member
       * entirely: three mages near a pool put thirty metres of penalty around it
       * and the fourth concludes that home is better. The point of a claim is
       * "somebody is already standing here", which is a fact about one square,
       * not a force field that gets stronger with the crowd.
       */
      let crowded = 0;
      for (const taken of others) {
        crowded = Math.max(crowded, Math.max(0, 1 - dist2d(at, taken) / CLAIM_RADIUS));
      }
      extra -= crowded * CLAIM_COST;
      /*
       * The Arcanist is pushed off its own doorstep.
       *
       * Its own shrine is worth something to the side and therefore shows up in
       * the value field, and with nothing else nearby that was enough to keep an
       * Arcanist loitering at home for the whole match. It does not defend, it
       * cannot bias, and it charges from anybody's water — home is the one place
       * on the map it has no business being.
       */
      if (self.cls === 'arcanist' && home) {
        extra -= Math.max(0, 1 - dist2d(at, home.pos) / 22) * HOMEBODY_COST;
      }
      /*
       * And the Cleric is drawn to it, for the same reason inverted.
       *
       * A shrine is worth almost nothing while nobody is attacking it, which is
       * right for everybody else and wrong for the class whose job is to be
       * there when somebody does. Without this a Cleric with no patients wanders
       * its whole anchor radius looking for something to be near.
       */
      if (self.cls === 'cleric' && home) {
        extra += Math.max(0, 1 - dist2d(at, home.pos) / 18) * HOMEBODY_PULL;
      }
      return extra;
    }, anchor) ?? (home ? { ...home.pos } : null);

    if (pick) this.claimed.set(self.id, pick);
    return pick;
  }

  /**
   * The jobs that are the class, which outrank positioning.
   *
   * Kept deliberately short. Everything that *can* be expressed as "which square
   * should I be on" belongs in the map, where it composes with everything else;
   * this is only the things that cannot — raising the dead, claiming water,
   * refilling a bar that has run out.
   */
  private duty(state: SimState, self: PlayerState): Vec3 | null {
    switch (self.cls) {
      case 'cleric': {
        // A ghost within reach is a mage back in the fight, which beats any
        // amount of standing somewhere sensible.
        const fallen = state.players
          .filter((p) => p.faction === self.faction && p.ghost && !p.inValhalla)
          .map((p) => ({ p, d: dist2d(self.pos, p.pos) }))
          .sort((a, b) => a.d - b.d)[0];
        if (fallen && fallen.d < 30) return { ...fallen.p.pos };

        // Somebody about to die. Not the merely scratched — that is positioning,
        // and the map already pulls a Cleric toward its own side.
        const dying = state.players
          .filter((p) => p.id !== self.id && p.faction === self.faction
            && isEmbodied(p) && p.hp < p.hpMax * 0.4)
          .map((p) => ({ p, d: dist2d(self.pos, p.pos) }))
          .sort((a, b) => a.d - b.d)[0];
        if (dying && dying.d < 26) return { ...dying.p.pos };
        return null;
      }

      case 'magician': {
        // Water underfoot that is not yet ours. Biasing is slow and
        // interruptible, so wandering off half way through wastes the lot.
        const here = state.arena.pools.find(
          (pool) => dist2d(self.pos, pool.pos) <= pool.innerRadius * 1.6,
        );
        if (here && here.owner !== self.faction) return { ...here.pos };
        return null;
      }

      case 'arcanist': {
        // Ley holds twenty seconds after leaving water and then falls off a
        // cliff. An Arcanist caught dry is a mage with no spells, so topping up
        // outranks any target — there is no kill at the end of the walk without
        // it.
        if (self.ley < ARCANIST_TOP_UP) {
          const water = this.nearestPool(state, self.pos);
          if (water) return { ...water.pos };
        }
        return null;
      }

      default:
        return null;
    }
  }

  /**
   * The Arcanist roams
  /** The nearest pool of any allegiance — an Arcanist charges from all of them. */
  private nearestPool(state: SimState, at: Vec3) {
    return state.arena.pools
      .map((pool) => ({ pool, d: dist2d(at, pool.pos) }))
      .sort((a, b) => a.d - b.d)[0]?.pool ?? null;
  }

  /**
   * Whether this bot is standing on something worth standing on.
   *
   * Pools by their inner radius, which is the ground that actually counts for
   * biasing, and shrines by the field a mage has to be inside to conquer or
   * resurrect at. Deliberately tight: this suspends spacing, and suspending it
   * across a whole room would put the team back in one heap.
   */
  private onObjective(state: SimState, self: PlayerState): { pos: Vec3; radius: number } | null {
    for (const pool of state.arena.pools) {
      if (dist2d(self.pos, pool.pos) <= pool.innerRadius + 0.5) {
        return { pos: pool.pos, radius: pool.innerRadius };
      }
    }
    for (const shrine of state.arena.shrines) {
      if (dist2d(self.pos, shrine.pos) <= SHRINE_FIELD) {
        return { pos: shrine.pos, radius: SHRINE_FIELD };
      }
    }
    return null;
  }

  /**
   * Keeps a spacing nudge from pushing a mage off the thing it came for.
   *
   * Suspending spacing outright on an objective was the obvious reading of
   * "objectives take precedence", and it produced two Clerics standing in
   * precisely the same square for the whole match — which is a worse answer to
   * area damage than the dance was.
   *
   * What is actually wanted is narrower: a mage may be moved *within* the water
   * it is claiming, never out of it. So a push that would carry it past the rim
   * is turned along the rim instead, and two Magicians end up on opposite sides
   * of the same pool, both still biasing it.
   */
  private keepOnObjective(
    self: PlayerState, away: Vec3, on: { pos: Vec3; radius: number },
  ): Vec3 | null {
    const out = { x: self.pos.x - on.pos.x, z: self.pos.z - on.pos.z };
    const reach = Math.hypot(out.x, out.z);
    if (reach < 1e-3) return away;

    const outward = (away.x * out.x + away.z * out.z) / reach;
    // Only the part heading out of the objective is a problem, and only once
    // there is no room left to be pushed into.
    if (outward <= 0 || reach + outward * 0.5 <= on.radius) return away;

    /*
     * Nowhere left to go, so stand still.
     *
     * This used to turn the push along the rim so nobody was shoved off the
     * pool, which sounds right and is the wiggle: two mages orbit the centre
     * without ever getting further apart, so the urge never clears and they
     * circle each other for as long as they both stand there.
     *
     * Standing shoulder to shoulder on an objective is the correct answer. It is
     * the ground being contested — that is *why* they are both there — and being
     * slightly more vulnerable to one Fire Ball is a much smaller cost than two
     * mages visibly jittering against each other for the whole match.
     */
    return null;
  }

  /** Friendlies close enough to be fighting the same fight. */
  private support(state: SimState, self: PlayerState, at: Vec3 = self.pos): PlayerState[] {
    return state.players.filter(
      (p) => p.id !== self.id && p.faction === self.faction && isEmbodied(p)
        && dist2d(p.pos, at) <= SUPPORT_RANGE,
    );
  }

  // ── Aiming ─────────────────────────────────────────────────────────────────

  private aim(
    state: SimState, self: PlayerState, mind: BotMind, input: PlayerInput, dt: number,
  ): void {
    // The hand drifts. Held for a fraction of a second at a time so the aim
    // wanders rather than vibrating, and long enough that a burst can miss.
    mind.aimHoldFor -= dt;
    if (mind.aimHoldFor <= 0) {
      mind.aimHoldFor = 0.25 + mind.rng.next() * 0.35;
      mind.aimOffset = {
        yaw: (mind.rng.next() - 0.5) * 2 * mind.skill.aimError,
        pitch: (mind.rng.next() - 0.5) * 2 * mind.skill.aimError,
      };
    }
    const target = mind.targetId
      ? state.players.find((p) => p.id === mind.targetId)
      : undefined;

    let at: Vec3 | null = null;
    if (target && isEmbodied(target)) {
      const spell = SPELLS_BY_ID[self.loadout[mind.slot] ?? ''];
      const speed = spell?.projectileSpeed ?? 40;
      const flight = dist(self.pos, target.pos) / speed;
      // Led off the target's own impulse velocity, which is the only movement
      // core actually knows about. Rough on purpose — perfect leading is not the
      // brief, and a bot that always lands a moving shot is not fun.
      at = {
        x: target.pos.x + target.vel.x * flight * mind.skill.lead,
        y: target.pos.y + 1.1,
        z: target.pos.z + target.vel.z * flight * mind.skill.lead,
      };
    } else if (mind.lastSeen) {
      at = { ...mind.lastSeen, y: mind.lastSeen.y + 1.1 };
    }

    if (!at) {
      // Nothing to shoot: face the way they are walking.
      const next = mind.path[0];
      if (next) {
        input.yaw = turnToward(
          self.yaw, Math.atan2(-(next.x - self.pos.x), -(next.z - self.pos.z)), dt,
        );
      } else {
        /*
         * Standing still, so face the room rather than the masonry.
         *
         * A Cleric holding a corner arrives, stops, and keeps whatever heading
         * the last step left it with — which for a post chosen *for* its cover is
         * usually straight into the wall behind it. A sentry watching the
         * brickwork is both useless and unmistakably a bug to anyone watching.
         */
        input.yaw = turnToward(self.yaw, this.faceOpen(self) ?? self.yaw, dt);
      }
      input.pitch = 0;
      return;
    }

    const eye = { x: self.pos.x, y: self.pos.y + 1.55, z: self.pos.z };
    const dx = at.x - eye.x;
    const dy = at.y - eye.y;
    const dz = at.z - eye.z;
    const len = Math.hypot(dx, dy, dz) || 1;

    input.yaw = turnToward(
      self.yaw,
      Math.atan2(-dx / len, -dz / len) + mind.aimOffset.yaw,
      dt,
    );
    input.pitch = Math.asin(Math.max(-1, Math.min(1, dy / len))) + mind.aimOffset.pitch;
  }

  /** Faces a point, without the leading and error a live bot applies. */
  /**
   * Combat movement: sidestep while holding ground.
   *
   * Only when there is something to fight, and only across the line of fire —
   * a bot that wanders while it has arrived somewhere it chose has given up the
   * ground it chose. So it strafes about its own position, changing direction on
   * a slow timer, which is enough to spoil a lead and nowhere near enough to
   * take it off a pool.
   *
   * Deliberately not dodging. Dodging a specific projectile needs the projectile
   * and reads as inhuman when it works; a mage that simply will not stand still
   * is most of the benefit and none of the uncanniness.
   */
  private jink(self: PlayerState, mind: BotMind, dt: number): Vec3 | null {
    if (!mind.targetId && !mind.lastSeen) return null;

    mind.jinkFor -= dt;
    if (mind.jinkFor <= 0) {
      mind.jinkFor = JINK_HOLD * (0.6 + mind.rng.next() * 0.8);
      mind.jinkSide = mind.jinkSide === 1 ? -1 : 1;
    }

    const at = mind.lastSeen;
    if (!at) return null;
    const dx = at.x - self.pos.x;
    const dz = at.z - self.pos.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) return null;
    // Across the line to the enemy, not toward or away from it.
    return { x: (-dz / len) * mind.jinkSide, y: 0, z: (dx / len) * mind.jinkSide };
  }

  /** A stable direction per bot, for breaking ties nothing else can break. */
  private bearingOf(id: string): number {
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) % 9973;
    return hash % 8;
  }

  /**
   * Which way there is most room to look.
   *
   * Eight rays, the longest wins. Cheap, needs nothing to be labelled, and gets
   * a mage in a corner facing out of it — which is all that is being asked. It
   * is not a threat assessment: where the danger comes from is `lastSeen`'s job,
   * and this only runs when there is no such thing.
   */
  private faceOpen(self: PlayerState): number | null {
    const eye = { x: self.pos.x, y: self.pos.y + 1.2, z: self.pos.z };
    let best: number | null = null;
    let bestClear = 0;

    for (let i = 0; i < 8; i++) {
      const yaw = i * (Math.PI / 4);
      const to = {
        x: eye.x - Math.sin(yaw) * FACE_PROBE,
        y: eye.y,
        z: eye.z - Math.cos(yaw) * FACE_PROBE,
      };
      // Binary, so ties are broken by the half-step search below rather than by
      // a distance this has no cheap way to measure.
      const clear = this.world.segmentBlocked(eye, to) ? 0 : 1;
      if (clear <= bestClear) continue;
      bestClear = clear;
      best = yaw;
    }
    return best;
  }

  private aimAt(self: PlayerState, at: Vec3, input: PlayerInput): void {
    const dx = at.x - self.pos.x;
    const dz = at.z - self.pos.z;
    input.yaw = Math.atan2(-dx, -dz);
    input.pitch = 0;
  }

  // ── Walking ────────────────────────────────────────────────────────────────

  private move(
    state: SimState, self: PlayerState, mind: BotMind, input: PlayerInput, dt: number,
  ): void {
    const next = mind.path[0];
    if (!next) {
      /*
       * Nowhere to be is not the same as nothing to do.
       *
       * This is the hole the influence map exposed. The old ladders nearly
       * always named a goal somewhere else, so "no path" was a rare edge; the
       * map frequently answers "right where you are standing", and a bot with no
       * path did *nothing at all* — no strafing, no repositioning, no getting
       * out of a friend's lap. That is most of what looked like passivity.
       *
       * Two urges, in order. Getting off somebody's toes comes first because
       * being stacked is the one that kills; otherwise a mage in a fight keeps
       * moving, because a stationary target in a spell game is a dead one.
       */
      const away = this.spacing(state, self, mind, dt);
      const drift = away ?? this.jink(self, mind, dt);
      if (drift) {
        const f = forwardVector(input.yaw, 0);
        const r = rightVector(input.yaw);
        input.moveForward = drift.x * f.x + drift.z * f.z;
        input.moveRight = drift.x * r.x + drift.z * r.z;
      }
      return;
    }

    if (dist2d(self.pos, next) < this.nav.cell * 0.7) {
      mind.path.shift();
      return;
    }

    // Movement is camera-relative, and a bot's aim rarely points where it is
    // walking — so the world-space direction has to be decomposed against the
    // yaw it is actually looking along, or bots strafe into walls whenever they
    // shoot at something beside them.
    const dx = next.x - self.pos.x;
    const dz = next.z - self.pos.z;
    const len = Math.hypot(dx, dz) || 1;
    const f = forwardVector(input.yaw, 0);
    const r = rightVector(input.yaw);

    const away = this.spacing(state, self, mind, dt);
    let wx = dx / len + (away?.x ?? 0) * SPACING_PUSH;
    let wz = dz / len + (away?.z ?? 0) * SPACING_PUSH;
    const spread = Math.hypot(wx, wz) || 1;
    wx /= spread;
    wz /= spread;

    input.moveForward = wx * f.x + wz * f.z;
    input.moveRight = wx * r.x + wz * r.z;
    // Runs when there is ground to cover and nothing to shoot; walks in a fight,
    // because running widens the cone it is already scattering by.
    input.run = mind.targetId === null && mind.path.length > 2;
  }

  /**
   * Which way to lean to stop sharing a tile with a friend.
   *
   * Bots converging on an objective converge on a *point*, and a heap of mages
   * is one Fire Ball. Deliberately not a formation: formations need every bot to
   * agree on one, and this needs nothing but the two mages involved — which
   * means it works between classes, between goals, and while standing still.
   */
  private spacing(
    state: SimState, self: PlayerState, mind: BotMind, dt: number,
  ): Vec3 | null {
    /*
     * On an objective, two mages are *supposed* to be close.
     *
     * Standing in the water is the reason the match is being played, so a mage
     * on one gets a far tighter idea of crowding: enough not to occupy the same
     * square, and no more. The previous attempt kept the full spacing and
     * projected the push sideways so nobody was shoved off the pool — which
     * meant two bots orbited its centre without ever getting further apart, the
     * urge never cleared, and they wiggled against each other for as long as
     * they both stood there.
     *
     * A pool's radius is metres and this asks for one, so the nudge fits inside
     * the objective and needs no sideways trick to keep them on it.
     */
    const on = this.onObjective(state, self);
    const enter = on ? OBJECTIVE_ENTER : SPACING_ENTER;
    const exit = on ? OBJECTIVE_EXIT : SPACING_EXIT;

    /*
     * Hysteresis, or two bots dance.
     *
     * A single threshold makes crowding a switch: push apart, cross the line,
     * stop pushing, drift back, push again — several times a second, which reads
     * as two mages shuffling around each other and is worst exactly where bots
     * legitimately share ground, in a pool or over a patient. So crowding starts
     * at one distance and stops at a wider one, and a bot that has just separated
     * stays quiet for a moment rather than immediately re-deciding.
     */
    mind.spacingRest = Math.max(0, mind.spacingRest - dt);
    const limit = mind.spacingUrge ? exit : enter;

    let x = 0;
    let z = 0;
    let closest = Infinity;
    for (const mate of mind.crowd) {
      const ax = self.pos.x - mate.x;
      const az = self.pos.z - mate.z;
      const gap = Math.hypot(ax, az);
      /*
       * Exactly on top of each other, which has no direction to push along.
       *
       * Skipping it looks harmless and is the one case that matters: two bots
       * spawned on the same square, or converging on the same best cell, sit
       * there for ever because the only rule that would move them divides by the
       * distance between them. A fixed per-bot bearing breaks the tie, and it is
       * derived from the name so the two never pick the same one.
       */
      if (gap < 1e-3) {
        const bearing = (this.bearingOf(self.id) * Math.PI) / 4;
        closest = 0;
        x += Math.cos(bearing);
        z += Math.sin(bearing);
        continue;
      }
      closest = Math.min(closest, gap);
      if (gap > limit) continue;
      const push = 1 - gap / limit;
      x += (ax / gap) * push;
      z += (az / gap) * push;
    }

    if (closest > exit) {
      if (mind.spacingUrge) mind.spacingRest = SPACING_REST;
      mind.spacingUrge = false;
      return null;
    }
    if (mind.spacingRest > 0) return null;
    mind.spacingUrge = closest <= limit;
    if (!mind.spacingUrge) return null;

    /*
     * A shove that is getting nowhere is stopped.
     *
     * The general safety net behind the tighter thresholds above: whatever the
     * geometry, if a bot has been pushing for a couple of seconds and the mage
     * it is pushing away from is no further off than when it started, the push
     * is not working and continuing it is just motion. Two bots wedged in a
     * doorway would do this as readily as two in a pool.
     */
    mind.spacingFor = closest < mind.spacingWas - 0.15 ? 0 : mind.spacingFor + dt;
    mind.spacingWas = closest;
    if (mind.spacingFor > SPACING_FUTILE) {
      mind.spacingUrge = false;
      mind.spacingRest = SPACING_REST * 3;
      return null;
    }

    const len = Math.hypot(x, z);
    if (len <= 1e-3) return null;

    /*
     * Proportional, not on-or-off. This is what stops the wiggle.
     *
     * The direction was right and the *speed* was the bug: a nudge was handed to
     * the movement input at full magnitude, so a bot separating did it at a
     * walk — a metre in under two hundred milliseconds, which crosses the whole
     * hysteresis band before anything can re-decide. Out at four metres it
     * stopped, the goal pulled it back under three, and it pushed again, five or
     * six times a second.
     *
     * Scaled by how far inside the band the nearest mage is, and capped low, it
     * becomes a drift: a bot a hair too close eases apart over a second or two
     * and settles, because it is no longer overshooting the line it is trying to
     * reach.
     */
    const urgency = Math.min(1, Math.max(0, (limit - closest) / limit));
    const push = SPACING_DRIFT * urgency;

    const away = { x: (x / len) * push, y: 0, z: (z / len) * push };
    return on ? this.keepOnObjective(self, away, on) : away;
  }

  // ── Casting ────────────────────────────────────────────────────────────────

  private act(
    state: SimState, self: PlayerState, mind: BotMind, input: PlayerInput, replan: boolean,
  ): void {
    // Call Dead has a range, so this only fires when actually standing over
    // somebody — and only when nothing is being shot at.
    if (CLASSES[self.cls].id === 'cleric' && !mind.targetId) {
      const near = state.players.some(
        (p) => p.faction === self.faction && p.ghost && !p.inValhalla
          && dist(self.pos, p.pos) <= 7,
      );
      if (near) {
        // Raise whoever is already at their feet before calling another. A
        // Cleric that called repeatedly would gather a crowd and never spend the
        // power to stand any of them up.
        const atFeet = state.players.some(
          (p) => p.faction === self.faction && p.ghost && !p.inValhalla
            && dist(self.pos, p.pos) <= RESURRECT_RANGE,
        );
        if (atFeet) input.resurrect = true;
        else input.callGhost = true;
        return;
      }
    }

    // Held while a wind-up runs. Readying a different slot abandons a cast, and
    // re-picking every tick meant a bot could never finish one — every spell
    // with a cast time was cancelled the frame after it began.
    if (!self.casting && replan) mind.slot = this.chooseSlot(state, self, mind);
    input.selectSlot = mind.slot;

    const spell = SPELLS_BY_ID[self.loadout[mind.slot] ?? ''];
    if (!spell) return;

    // Everything waits on the reaction, a guard included. Shielding on the exact
    // frame an enemy appears is as inhuman as shooting on it.
    if (!mind.targetId || mind.reactionLeft > 0) return;

    input.fire = true;
  }

  /**
   * Which spell to reach for.
   *
   * Deliberately shallow: heal when hurt, guard when a fight begins, area when
   * two enemies are standing together, otherwise the primary. Walls, signs and
   * the rest are left alone until bots are worth making clever.
   */
  private chooseSlot(state: SimState, self: PlayerState, mind: BotMind): number {
    const usable = (i: number): boolean => {
      const spell = SPELLS_BY_ID[self.loadout[i] ?? ''];
      if (!spell) return false;
      if (tierInSlot(self, spell.slot) <= 0) return false;
      if (self.cooldowns[i] > 0) return false;
      const tier = Math.max(0, tierInSlot(self, spell.slot) - 1);
      return self.pp >= spell.ppCost[Math.min(spell.ppCost.length - 1, tier)];
    };
    const slotOf = (role: string): number => SLOT_ORDER.indexOf(role as never);

    // Hurt: mend before anything else.
    if (self.hp < self.hpMax * 0.5) {
      for (const role of ['defense', 'control'] as const) {
        const i = slotOf(role);
        const spell = SPELLS_BY_ID[self.loadout[i] ?? ''];
        if (spell?.kind === 'heal' && usable(i)) return i;
      }
    }

    // A fight starting, and no guard up: put one up.
    if (mind.targetId && !self.effects.some((e) => e.protection)) {
      const i = slotOf('defense');
      const spell = SPELLS_BY_ID[self.loadout[i] ?? ''];
      if (spell?.kind === 'buff' && usable(i)) return i;
    }

    // Two or more enemies standing together is what an area spell is for.
    if (mind.targetId && usable(slotOf('area'))) {
      const target = state.players.find((p) => p.id === mind.targetId);
      if (target) {
        const clustered = state.players.filter(
          (p) => isEmbodied(p) && p.faction !== self.faction
            && dist2d(p.pos, target.pos) < 5,
        ).length;
        if (clustered >= 2) return slotOf('area');
      }
    }

    // Rolled on a thinking tick only, which is what makes a bot commit to a
    // secondary long enough for its wind-up to finish.
    if (usable(slotOf('secondary')) && mind.rng.next() < 0.35) return slotOf('secondary');
    if (usable(slotOf('primary'))) return slotOf('primary');
    return mind.slot;
  }
}
